// src/components/ServiceTab.jsx
// Client Service Command Center - track carrier calls, outcomes, and follow-ups

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Phone,
  PhoneCall,
  PhoneOff,
  Clock,
  CheckCircle,
  AlertCircle,
  Calendar,
  ChevronDown,
  ChevronUp,
  Plus,
  RefreshCw,
  ExternalLink,
  X,
  FileText,
  Timer,
  Flag,
  MessageSquare,
  TrendingUp,
  Maximize2
} from 'lucide-react';

const BASE = process.env.REACT_APP_API_URL || '';

// Call reason options
const CALL_REASONS = [
  { value: 'claims_issue', label: 'Claims Issue', color: 'red' },
  { value: 'billing', label: 'Billing Question', color: 'orange' },
  { value: 'prior_auth', label: 'Prior Authorization', color: 'purple' },
  { value: 'enrollment_status', label: 'Enrollment Status', color: 'blue' },
  { value: 'id_card', label: 'ID Card Request', color: 'cyan' },
  { value: 'benefits_question', label: 'Benefits Question', color: 'teal' },
  { value: 'provider_search', label: 'Provider Search', color: 'green' },
  { value: 'rx_issue', label: 'Prescription Issue', color: 'pink' },
  { value: 'appeal', label: 'Appeal/Grievance', color: 'red' },
  { value: 'disenrollment', label: 'Disenrollment', color: 'slate' },
  { value: 'other', label: 'Other', color: 'gray' }
];

// Outcome options
const OUTCOMES = [
  { value: 'resolved', label: 'Resolved', icon: CheckCircle, color: 'green' },
  { value: 'pending', label: 'Pending', icon: Clock, color: 'yellow' },
  { value: 'escalated', label: 'Escalated', icon: TrendingUp, color: 'orange' },
  { value: 'callback_scheduled', label: 'Callback Scheduled', icon: Calendar, color: 'blue' },
  { value: 'no_answer', label: 'No Answer', icon: PhoneOff, color: 'red' }
];

export default function ServiceTab({ clientId, clientName, clientCarrier }) {
  const [events, setEvents] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewCall, setShowNewCall] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [clientStats, setClientStats] = useState(null);
  const [defaultCarrier, setDefaultCarrier] = useState(null);

  // New call form state
  const [selectedCarrier, setSelectedCarrier] = useState(null);
  const [callReason, setCallReason] = useState('');
  const [callReasonDetail, setCallReasonDetail] = useState('');
  const [phoneDialed, setPhoneDialed] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Outcome modal state
  const [outcomeModal, setOutcomeModal] = useState(null);
  const [outcome, setOutcome] = useState('');
  const [outcomeNotes, setOutcomeNotes] = useState('');
  const [requiresFollowUp, setRequiresFollowUp] = useState(false);
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpReason, setFollowUpReason] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');

  // Fetch carriers and events
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [carriersRes, eventsRes, statsRes] = await Promise.all([
        fetch(`${BASE}/service/carriers`, { credentials: 'include' }),
        fetch(`${BASE}/service/clients/${clientId}/events`, { credentials: 'include' }),
        fetch(`${BASE}/service/analytics/clients/${clientId}`, { credentials: 'include' })
      ]);

      if (carriersRes.ok) {
        const data = await carriersRes.json();
        const carrierList = data.carriers || [];
        setCarriers(carrierList);

        // Auto-match client's carrier from the directory
        if (clientCarrier && carrierList.length > 0) {
          const normalizedClientCarrier = clientCarrier.toLowerCase().trim();
          const matched = carrierList.find(c => {
            const name = c.carrier_name.toLowerCase();
            const code = (c.carrier_code || '').toLowerCase();
            // Match by name, code, or partial match
            return name === normalizedClientCarrier ||
                   code === normalizedClientCarrier ||
                   name.includes(normalizedClientCarrier) ||
                   normalizedClientCarrier.includes(name) ||
                   normalizedClientCarrier.includes(code);
          });
          if (matched) {
            setDefaultCarrier(matched);
          }
        }
      }
      if (eventsRes.ok) {
        const data = await eventsRes.json();
        setEvents(data.events || []);
      }
      if (statsRes.ok) {
        const data = await statsRes.json();
        setClientStats(data.analytics || null);
      }
    } catch (err) {
      console.error('Failed to load service data:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId, clientCarrier]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Open new call modal with default carrier pre-selected
  function openNewCallModal() {
    if (defaultCarrier) {
      setSelectedCarrier(defaultCarrier);
      setPhoneDialed(getCarrierPhone(defaultCarrier));
    }
    setShowNewCall(true);
  }

  // Submit new carrier call
  async function handleSubmitCall(e) {
    e.preventDefault();
    if (!selectedCarrier || !callReason) return;

    setSubmitting(true);
    try {
      const res = await fetch(`${BASE}/service/clients/${clientId}/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          event_type: 'carrier_call',
          carrier_id: selectedCarrier.id,
          carrier_name: selectedCarrier.carrier_name,
          phone_dialed: phoneDialed || selectedCarrier.general_support_phone,
          call_reason: callReason,
          call_reason_detail: callReasonDetail
        })
      });

      if (res.ok) {
        const data = await res.json();
        setEvents([data.event, ...events]);
        setShowNewCall(false);
        setSelectedCarrier(null);
        setCallReason('');
        setCallReasonDetail('');
        setPhoneDialed('');
        // Open outcome modal for the new call
        setOutcomeModal(data.event);
      }
    } catch (err) {
      console.error('Failed to create service event:', err);
    } finally {
      setSubmitting(false);
    }
  }

  // Submit outcome
  async function handleSubmitOutcome(e) {
    e.preventDefault();
    if (!outcomeModal || !outcome) return;

    try {
      const res = await fetch(`${BASE}/service/events/${outcomeModal.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          outcome,
          outcome_notes: outcomeNotes,
          requires_follow_up: requiresFollowUp,
          follow_up_date: followUpDate || null,
          follow_up_reason: followUpReason,
          reference_number: referenceNumber
        })
      });

      if (res.ok) {
        const data = await res.json();
        setEvents(events.map(ev => ev.id === data.event.id ? data.event : ev));
        setOutcomeModal(null);
        setOutcome('');
        setOutcomeNotes('');
        setRequiresFollowUp(false);
        setFollowUpDate('');
        setFollowUpReason('');
        setReferenceNumber('');
      }
    } catch (err) {
      console.error('Failed to update outcome:', err);
    }
  }

  // Format relative time
  function formatRelativeTime(dateStr) {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  }

  // Get phone number for carrier
  function getCarrierPhone(carrier) {
    return carrier.ma_support_phone || carrier.medsup_support_phone || carrier.general_support_phone || '';
  }

  // Get reason color
  function getReasonColor(reason) {
    const found = CALL_REASONS.find(r => r.value === reason);
    return found?.color || 'gray';
  }

  // Get outcome details
  function getOutcomeDetails(outcomeValue) {
    return OUTCOMES.find(o => o.value === outcomeValue) || { label: outcomeValue, color: 'gray', icon: FileText };
  }

  return (
    <div className="bg-gradient-to-br from-white to-slate-50 rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between p-6 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white shadow-lg">
            <PhoneCall size={20} />
          </div>
          <div>
            <h3 className="font-bold text-lg text-[#172A3A]">Service Center</h3>
            <p className="text-sm text-slate-500">
              {defaultCarrier ? (
                <>Carrier: <span className="font-medium text-indigo-600">{defaultCarrier.carrier_name}</span></>
              ) : clientCarrier ? (
                <>Carrier: {clientCarrier}</>
              ) : (
                'Track carrier calls & outcomes'
              )}
            </p>
          </div>
          {clientStats && clientStats.total_events > 0 && (
            <span className="ml-3 px-2.5 py-1 bg-indigo-100 text-indigo-700 text-xs font-semibold rounded-full">
              {clientStats.total_events} events
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/service/client/${clientId}`}
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Maximize2 size={14} /> Open Workspace
          </Link>
          <button
            onClick={(e) => { e.stopPropagation(); openNewCallModal(); }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Plus size={16} /> Log Call
          </button>
          {expanded ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-slate-100">
          {/* Carrier Quick Actions Bar */}
          {defaultCarrier && (
            <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-slate-100">
              <span className="text-sm font-medium text-slate-600">{defaultCarrier.carrier_name}</span>
              <div className="flex items-center gap-2 ml-auto">
                {getCarrierPhone(defaultCarrier) && (
                  <a
                    href={`tel:${getCarrierPhone(defaultCarrier)}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-lg transition-colors"
                  >
                    <Phone size={14} /> {getCarrierPhone(defaultCarrier)}
                  </a>
                )}
                {defaultCarrier.portal_url && (
                  <a
                    href={defaultCarrier.portal_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-lg transition-colors"
                  >
                    <ExternalLink size={14} /> Agent Portal
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Quick Stats */}
          {clientStats && clientStats.total_events > 0 && (
            <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50/50 border-b border-slate-100">
              <div className="text-center">
                <div className="text-2xl font-bold text-[#172A3A]">{clientStats.total_events}</div>
                <div className="text-xs text-slate-500">Total Calls</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {clientStats.resolution_rate !== null ? `${clientStats.resolution_rate}%` : '-'}
                </div>
                <div className="text-xs text-slate-500">Resolved</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-[#172A3A]">
                  {clientStats.common_issues?.[0]?.call_reason?.replace(/_/g, ' ') || '-'}
                </div>
                <div className="text-xs text-slate-500">Top Issue</div>
              </div>
            </div>
          )}

          {/* Events List */}
          <div className="p-4">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
              </div>
            ) : events.length === 0 ? (
              <div className="text-center py-8">
                <Phone className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No service calls logged yet</p>
                <button
                  onClick={() => openNewCallModal()}
                  className="mt-3 text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                >
                  Log your first carrier call
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {events.slice(0, 5).map(event => {
                  const outcomeDetails = event.outcome ? getOutcomeDetails(event.outcome) : null;
                  const OutcomeIcon = outcomeDetails?.icon || Clock;

                  return (
                    <div
                      key={event.id}
                      className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-[#172A3A]">
                              {event.carrier_name || 'Unknown Carrier'}
                            </span>
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full bg-${getReasonColor(event.call_reason)}-100 text-${getReasonColor(event.call_reason)}-700`}>
                              {CALL_REASONS.find(r => r.value === event.call_reason)?.label || event.call_reason}
                            </span>
                          </div>
                          {event.call_reason_detail && (
                            <p className="text-sm text-slate-600 mb-2">{event.call_reason_detail}</p>
                          )}
                          <div className="flex items-center gap-3 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <Clock size={12} />
                              {formatRelativeTime(event.created_at)}
                            </span>
                            {event.phone_dialed && (
                              <span className="flex items-center gap-1">
                                <Phone size={12} />
                                {event.phone_dialed}
                              </span>
                            )}
                            {event.reference_number && (
                              <span className="flex items-center gap-1">
                                <FileText size={12} />
                                Ref: {event.reference_number}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {event.outcome ? (
                            <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-${outcomeDetails?.color}-100 text-${outcomeDetails?.color}-700`}>
                              <OutcomeIcon size={14} />
                              {outcomeDetails?.label}
                            </span>
                          ) : (
                            <button
                              onClick={() => setOutcomeModal(event)}
                              className="flex items-center gap-1 px-2.5 py-1 bg-amber-100 hover:bg-amber-200 text-amber-700 text-xs font-medium rounded-lg transition-colors"
                            >
                              <Flag size={14} />
                              Add Outcome
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Outcome notes */}
                      {event.outcome_notes && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <p className="text-sm text-slate-600">
                            <MessageSquare size={12} className="inline mr-1" />
                            {event.outcome_notes}
                          </p>
                        </div>
                      )}

                      {/* Follow-up indicator */}
                      {event.requires_follow_up && !event.follow_up_completed_at && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar size={14} className="text-blue-500" />
                            <span className="text-blue-700 font-medium">
                              Follow-up: {event.follow_up_date ? new Date(event.follow_up_date).toLocaleDateString() : 'Pending'}
                            </span>
                            {event.follow_up_reason && (
                              <span className="text-slate-500">- {event.follow_up_reason}</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {events.length > 5 && (
                  <button className="w-full py-2 text-center text-sm text-indigo-600 hover:text-indigo-700 font-medium">
                    View all {events.length} service events
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* New Call Modal */}
      {showNewCall && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-[#172A3A]">Log Carrier Call</h3>
                <button onClick={() => setShowNewCall(false)} className="text-slate-400 hover:text-slate-600">
                  <X size={24} />
                </button>
              </div>
              <p className="text-sm text-slate-500 mt-1">
                Track a call to a carrier on behalf of {clientName}
              </p>
            </div>

            <form onSubmit={handleSubmitCall} className="p-6 space-y-4">
              {/* Carrier Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Select Carrier *</label>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  {carriers.map(carrier => (
                    <button
                      key={carrier.id}
                      type="button"
                      onClick={() => {
                        setSelectedCarrier(carrier);
                        setPhoneDialed(getCarrierPhone(carrier));
                      }}
                      className={`p-3 text-left rounded-lg border-2 transition-colors ${
                        selectedCarrier?.id === carrier.id
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-slate-200 hover:border-indigo-300'
                      }`}
                    >
                      <div className="font-medium text-sm text-[#172A3A]">{carrier.carrier_name}</div>
                      <div className="text-xs text-slate-500">{carrier.carrier_code}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Carrier Quick Actions */}
              {selectedCarrier && (
                <div className="space-y-3">
                  {/* Click-to-Call & Agent Portal */}
                  <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100">
                    <label className="block text-sm font-semibold text-indigo-900 mb-3">Quick Actions</label>
                    <div className="flex gap-2">
                      <a
                        href={`tel:${phoneDialed}`}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm"
                      >
                        <Phone size={18} /> Call Carrier
                      </a>
                      {selectedCarrier.portal_url && (
                        <a
                          href={selectedCarrier.portal_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm"
                        >
                          <ExternalLink size={18} /> Agent Portal
                        </a>
                      )}
                    </div>
                    {/* Phone number display / edit */}
                    <div className="mt-3">
                      <input
                        type="text"
                        value={phoneDialed}
                        onChange={(e) => setPhoneDialed(e.target.value)}
                        className="w-full px-3 py-2 border border-indigo-200 rounded-lg text-sm bg-white"
                        placeholder="Phone number"
                      />
                    </div>
                    {selectedCarrier.hours_of_operation && (
                      <p className="text-xs text-indigo-600 mt-2">
                        <Clock size={10} className="inline mr-1" />
                        Hours: {selectedCarrier.hours_of_operation}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Call Reason */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Call Reason *</label>
                <select
                  value={callReason}
                  onChange={(e) => setCallReason(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  required
                >
                  <option value="">Select reason...</option>
                  {CALL_REASONS.map(reason => (
                    <option key={reason.value} value={reason.value}>{reason.label}</option>
                  ))}
                </select>
              </div>

              {/* Reason Detail */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Details</label>
                <textarea
                  value={callReasonDetail}
                  onChange={(e) => setCallReasonDetail(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  placeholder="Add any relevant details about this call..."
                />
              </div>

              {/* Submit */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowNewCall(false)}
                  className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!selectedCarrier || !callReason || submitting}
                  className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Logging...' : 'Log Call'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Outcome Modal */}
      {outcomeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-[#172A3A]">Call Outcome</h3>
                <button onClick={() => setOutcomeModal(null)} className="text-slate-400 hover:text-slate-600">
                  <X size={24} />
                </button>
              </div>
              <p className="text-sm text-slate-500 mt-1">
                {outcomeModal.carrier_name} - {CALL_REASONS.find(r => r.value === outcomeModal.call_reason)?.label}
              </p>
            </div>

            <form onSubmit={handleSubmitOutcome} className="p-6 space-y-4">
              {/* Outcome Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Outcome *</label>
                <div className="grid grid-cols-2 gap-2">
                  {OUTCOMES.map(opt => {
                    const Icon = opt.icon;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setOutcome(opt.value)}
                        className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-colors ${
                          outcome === opt.value
                            ? `border-${opt.color}-500 bg-${opt.color}-50`
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <Icon size={18} className={`text-${opt.color}-600`} />
                        <span className="text-sm font-medium">{opt.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Reference Number */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Reference/Case Number</label>
                <input
                  type="text"
                  value={referenceNumber}
                  onChange={(e) => setReferenceNumber(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  placeholder="Carrier case or reference number"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Notes</label>
                <textarea
                  value={outcomeNotes}
                  onChange={(e) => setOutcomeNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  placeholder="What happened on the call?"
                />
              </div>

              {/* Follow-up */}
              <div className="bg-slate-50 rounded-lg p-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={requiresFollowUp}
                    onChange={(e) => setRequiresFollowUp(e.target.checked)}
                    className="w-4 h-4 text-indigo-600 rounded"
                  />
                  <span className="text-sm font-medium text-slate-700">Requires Follow-up</span>
                </label>

                {requiresFollowUp && (
                  <div className="mt-3 space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Follow-up Date</label>
                      <input
                        type="date"
                        value={followUpDate}
                        onChange={(e) => setFollowUpDate(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Follow-up Reason</label>
                      <input
                        type="text"
                        value={followUpReason}
                        onChange={(e) => setFollowUpReason(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                        placeholder="Why do you need to follow up?"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Submit */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setOutcomeModal(null)}
                  className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!outcome}
                  className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Save Outcome
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
